/**
 * Thin fetch wrapper for the kpi-orchestrator.
 *
 * Every request is tenant-scoped. Demo mode uses the effective tenant as
 * X-Tenant-Id; JWT mode sends both Authorization and X-Tenant-Id so backend
 * audit/event writes never run without an explicit tenant context.
 */
import { useAuth } from "./auth";
import { useTenant } from "./tenant";
import { useMemo } from "react";

const BASE = import.meta.env.VITE_API_BASE ?? "/v1";

export type ApiError = { status: number; message: string; details?: unknown };

export type AuthSnapshot = { token: string | null; tenantId: string | null };

export function buildTenantScopedHeaders(auth: AuthSnapshot, initHeaders?: HeadersInit): Headers {
  const tenantId = auth.tenantId?.trim() ?? "";
  if (!tenantId) {
    throw { status: 401, message: "missing tenantId" } satisfies ApiError;
  }

  const headers = new Headers(initHeaders);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  if (auth.token) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }
  headers.set("X-Tenant-Id", tenantId);

  return headers;
}

async function request<T>(auth: AuthSnapshot, path: string, init: RequestInit = {}): Promise<T> {
  const headers = buildTenantScopedHeaders(auth, init.headers);
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* noop */ }
    throw { status: res.status, message: `api ${res.status}`, details: body } satisfies ApiError;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function useApi() {
  const { token } = useAuth();
  const { effectiveTenantId } = useTenant();
  const auth: AuthSnapshot = { token, tenantId: effectiveTenantId };
  return useMemo(
    () => ({
      get: <T,>(p: string) => request<T>(auth, p),
      post: <T,>(p: string, body: unknown) => request<T>(auth, p, { method: "POST", body: JSON.stringify(body) }),
      put: <T,>(p: string, body: unknown) => request<T>(auth, p, { method: "PUT", body: JSON.stringify(body) }),
      patch: <T,>(p: string, body: unknown) => request<T>(auth, p, { method: "PATCH", body: JSON.stringify(body) }),
      del: <T,>(p: string) => request<T>(auth, p, { method: "DELETE" }),
    }),
    [token, effectiveTenantId],
  );
}

// Domain models — kept in sync with backend models.py
export type MetricDefinition = {
  id: string;
  name: string;
  display_name?: string;
  domain: string;
  unit: string;
  granularity: "hour" | "day" | "week" | "month" | "quarter" | "year";
  direction: "up_is_favorable" | "down_is_favorable" | "neutral";
  data_classification: "public" | "internal" | "confidential" | "restricted";
  target?: { value: number; tolerance_pct?: number };
};

export type Briefing = {
  id: string;
  type: "daily" | "weekly" | "monthly" | "ad_hoc";
  audience: "cfo" | "ceo" | "coo" | "controller" | "all";
  status: "queued" | "generating" | "ready" | "delivered" | "failed";
  markdown_url?: string;
  pdf_url?: string;
  created_at: string;
};

export type AgentRun = {
  run_id: string;
  tenant_id: string;
  agent: string;
  status: "queued" | "running" | "succeeded" | "failed" | "blocked";
  started_at: string;
  ended_at?: string;
  evidence_bundle?: string;
};

export type Source = {
  id: string;
  type: "airbyte" | "upload" | "rest" | "crawl4ai" | "nimble";
  name: string;
  config: Record<string, unknown>;
  last_sync?: string;
  status: "healthy" | "degraded" | "failed" | "unknown";
};

export type Insight = {
  id: string;
  type: "anomaly" | "trend_break" | "threshold" | "rca";
  metric_id: string;
  severity: "info" | "warn" | "critical";
  what_happened: string;
  why_hypothesis?: string;
  what_to_do: Array<{ action_template: string; expected_impact?: string }>;
  owner?: string;
  created_at: string;
};

export type Benchmark = {
  id: string;
  metric_name: string;
  source: string;
  license?: string;
  reference_value?: number;
  reference_period?: string;
  last_fetched_at?: string;
};

export type Tenant = {
  id: string;
  slug: string;
  display_name: string;
  brand: string;
  external_llm_allowed: boolean;
  keycloak_realm: string;
  data_classification_default: string;
  created_at: string;
};


// Onboarding payloads (used by Onboarding.tsx + TenantOnboarding.tsx)
export type TenantCreatePayload = {
  slug: string;
  display_name: string;
  brand: "ben-e-fit" | "medialine" | "ki-guru" | "custom";
  admin_email: string;
  external_llm_allowed?: boolean;
};
